import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, InstagramLogo, Camera, ArrowsLeftRight, ArrowRight } from "@phosphor-icons/react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { EnquiryDialog } from "../components/EnquiryDialog";

const BeforeAfter = ({ before, after, alt, testId }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const move = (x) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(97, Math.max(3, ((x - r.left) / r.width) * 100)));
  };
  return (
    <div
      ref={ref}
      data-testid={testId}
      className="relative aspect-[4/5] w-full cursor-ew-resize select-none overflow-hidden border border-border bg-muted"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons === 1) move(e.clientX);
      }}
    >
      <img src={after} alt={`${alt} — after`} className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={`${alt} — before`} className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      </div>
      <div className="absolute inset-y-0 w-px bg-white/90" style={{ left: `${pos}%` }} />
      <div
        className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-primary text-white shadow-lg"
        style={{ left: `${pos}%` }}
      >
        <ArrowsLeftRight size={17} weight="bold" />
      </div>
      <span className="absolute left-3 top-3 bg-black/50 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white">Before</span>
      <span className="absolute right-3 top-3 bg-black/50 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white">After</span>
    </div>
  );
};

export default function Gallery() {
  const CASES = [
    {
      id: "tear-trough",
      title: "Tear trough",
      accent: "rejuvenation.",
      desc: "Under-eye hollows and dark circles softened with Teosyal Redensity II — a specialised filler designed for the delicate tear trough area. Real DK client, shared with written consent, photographed in clinic with no filters or editing.",
      before: "/assets/ba-tear-before.jpg",
      after: "/assets/ba-tear-after.jpg",
      facts: [
        ["Treatment", "Tear Trough — Teosyal Redensity II"],
        ["Investment", "£350 per session"],
        ["Downtime", "None — back to work the same day"],
        ["Results last", "12 — 18 months"],
      ],
      treatLink: "/treatments#tear-trough",
      ig: [
        { url: "https://www.instagram.com/p/Dbd3smzRT4g/", label: "Post 1" },
        { url: "https://www.instagram.com/p/DYmitATAlkv/", label: "Post 2" },
      ],
    },
    {
      id: "hifu",
      title: "Face & neck",
      accent: "HIFU lift.",
      desc: "A visibly lifted jawline and firmer neck after a first HIFU session — non-surgical, non-invasive and no downtime. Results keep improving for 8–12 weeks as new collagen forms.",
      before: "/assets/ba-hifu-before.jpg",
      after: "/assets/ba-hifu-after.jpg",
      facts: [
        ["Treatment", "HIFU — Face & Neck"],
        ["Investment", "£450"],
        ["Downtime", "None"],
        ["Results last", "12 — 18 months"],
      ],
      treatLink: "/treatments#hifu",
      ig: [{ url: "https://www.instagram.com/dk_aestheticsuk/p/DCohpV7K4FS/", label: "Instagram post" }],
    },
    {
      id: "anti-wrinkle",
      title: "Forehead lines,",
      accent: "softened.",
      desc: "Anti-wrinkle injections, one area — forehead lines visibly evened out after her first session, with natural movement and expression preserved.",
      before: "/assets/ba-antiwrinkle-before.jpg",
      after: "/assets/ba-antiwrinkle-after.jpg",
      facts: [
        ["Treatment", "Anti-Wrinkle Injections — 1 Area"],
        ["Investment", "£150"],
        ["Downtime", "No recovery time"],
        ["Results last", "3 — 4 months"],
      ],
      treatLink: "/treatments#anti-wrinkle",
      ig: [{ url: "https://www.instagram.com/dk_aestheticsuk/p/DCohiMTq-IT/", label: "Instagram post" }],
    },
    {
      id: "lips",
      title: "Lip enhancement —",
      accent: "less is more.",
      desc: "1ml of dermal filler for a little plumpness and evened-out imperfections — subtle, balanced and completely natural-looking.",
      before: "/assets/ba-lips-before.jpg",
      after: "/assets/ba-lips-after.jpg",
      facts: [
        ["Treatment", "Lip Enhancement — 1ml"],
        ["Investment", "£200"],
        ["Downtime", "Minimal swelling"],
        ["Results last", "6 — 12 months"],
      ],
      treatLink: "/treatments#lips",
      ig: [{ url: "https://www.instagram.com/dk_aestheticsuk/p/DCohWpOqU_T/", label: "Instagram post" }],
    },
  ];

  return (
    <main data-testid="gallery-page">
      <section className="border-b border-border bg-secondary/40 pb-16 pt-40 sm:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal y={20}>
            <Eyebrow>Before & After</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-display text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              Real skin. <em className="text-primary">Real results.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-muted-foreground">
              Every result photograph we share is a real DK client, published with their written consent.
              Our full before-and-after gallery is being curated now — in the meantime, browse our latest
              results on Instagram.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="https://www.instagram.com/dk_aestheticsuk"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-instagram-button"
                className="group inline-flex w-fit items-center gap-2 bg-primary px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90"
              >
                <InstagramLogo size={16} />
                See results on Instagram
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <EnquiryDialog
                defaultType="consultation"
                trigger={
                  <button
                    data-testid="gallery-consultation-button"
                    className="border border-foreground px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-foreground hover:text-background"
                  >
                    Free consultation
                  </button>
                }
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border py-24 sm:py-28" data-testid="gallery-cases-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Featured results</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
              Drag the handle. <em className="text-primary">See the difference.</em>
            </h2>
          </Reveal>
          <div className="mt-16 space-y-24">
            {CASES.map((c, i) => (
              <div key={c.id} className="grid grid-cols-1 items-center gap-14 md:grid-cols-12" data-testid={`gallery-case-${c.id}`}>
                <Reveal className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <BeforeAfter before={c.before} after={c.after} alt={`${c.title} ${c.accent}`} testId={`ba-slider-${c.id}`} />
                  <p className="mt-3 text-center text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                    Drag the handle to compare
                  </p>
                </Reveal>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <Reveal>
                    <span className="font-display text-lg italic text-primary/50">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-2 font-display text-4xl font-light tracking-tight">
                      {c.title} <em className="text-primary">{c.accent}</em>
                    </h3>
                    <p className="mt-5 max-w-md text-base font-light leading-relaxed text-muted-foreground">{c.desc}</p>
                  </Reveal>
                  <Reveal delay={0.12}>
                    <ul className="mt-7 divide-y divide-border/70 border-y border-border/70">
                      {c.facts.map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-6 py-3">
                          <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{k}</span>
                          <span className="text-right text-sm font-light">{v}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                      <Link
                        to={c.treatLink}
                        data-testid={`gallery-case-treatment-${c.id}`}
                        className="group inline-flex items-center gap-2 bg-primary px-6 py-3.5 text-[11px] font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90"
                      >
                        Treatment details
                        <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                      {c.ig.map((p, j) => (
                        <a
                          key={p.url}
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`gallery-case-instagram-${c.id}-${j}`}
                          className="inline-flex items-center gap-2 border border-border px-5 py-3.5 text-[11px] font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:border-primary hover:text-primary"
                        >
                          <InstagramLogo size={14} /> {p.label}
                        </a>
                      ))}
                    </div>
                  </Reveal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="gallery-consent-section">
        <div className="grid grid-cols-1 items-center gap-14 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="/assets/clinic-injection.jpg"
                alt="Vitamin injection treatment at DK Aesthetics"
                className="aspect-[4/5] w-full object-cover"
                data-testid="gallery-image-room"
              />
              <img
                src="/assets/dee-scrubs.jpg"
                alt="Dee Kaur performing a treatment at DK Aesthetics"
                className="mt-12 aspect-[4/5] w-full object-cover"
                data-testid="gallery-image-treatment"
              />
            </div>
          </Reveal>
          <div className="md:col-span-5">
            <Reveal>
              <Eyebrow>Your privacy matters</Eyebrow>
              <h2 className="mt-4 font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
                Shared with consent, <em className="text-primary">always.</em>
              </h2>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-muted-foreground">
                We only publish before-and-after photographs with a client's explicit written permission,
                and images are never edited or filtered. During your consultation you're welcome to view
                our full in-clinic portfolio privately.
              </p>
              <p className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">
                <Camera size={15} className="text-primary" /> Full gallery launching soon
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
